import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Callout } from '@/shared/components/feedback/Callout';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { useCaseVerdictLogic } from '../../hooks/useCaseVerdictLogic/useCaseVerdictLogic';

/**
 * Approving a member — terminal for the case, and the moment they become
 * visible to everybody else.
 *
 * Short, and deliberately not a generic confirm. It names the account, says what
 * the member gains, and says that the case is finished: `approved` is terminal
 * in `VERIFICATION_CASE_TRANSITIONS`, so a later concern is a **new case** and
 * not a re-decision — which is exactly the thing an operator assumes wrongly and
 * finds out too late.
 *
 * The note is for colleagues, not for the member, and the label says so. The
 * member reads a reason only on a refusal.
 *
 * An approval with steps still open is legal and sometimes right — a face
 * match the machine could not settle, a reviewer overriding policy — but it is
 * also the click that once approved a member who had never been interviewed
 * (F7). So the open steps are named, in a warning, before the button.
 */
export function ApproveCaseModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation(['verification', 'common']);
  const { target, note, setNote, isDeciding, error, submit } = useCaseVerdictLogic(
    'approve',
    modalData,
    onCancel,
  );

  if (target === null) {
    return null;
  }

  const member = target.member ?? t('verification:queue.missingAccount');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('verification:approve.title')}</DialogTitle>
          <DialogDescription>{t('verification:detail.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* The address is repeated below with `dir="ltr"` rather than
              emphasised inline: an email is not localised, and a bidi run inside
              an Arabic sentence puts the @ somewhere confusing (P10.6j). */}
          <p className="text-body text-foreground">{t('verification:approve.lead', { member })}</p>

          <p dir="ltr" className="rounded-md bg-muted p-md text-body font-medium">
            {member}
          </p>

          <p className="flex items-start gap-sm text-body text-muted-foreground">
            <ShieldCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('verification:approve.terminal')}</span>
          </p>

          {target.outstandingSteps.length === 0 ? null : (
            <Callout
              tone="warning"
              liveness="off"
              title={t('verification:approve.outstanding.title')}
            >
              <span data-approve-outstanding>
                {t('verification:approve.outstanding.body', {
                  steps: target.outstandingSteps
                    .map((key) => t(`verification:step.key.${key}`))
                    .join(t('common:answer.listSeparator')),
                })}
              </span>
            </Callout>
          )}

          <div className="flex flex-col gap-xs">
            <Label htmlFor="verification-approve-note">{t('verification:approve.noteLabel')}</Label>
            <Textarea
              id="verification-approve-note"
              aria-describedby="verification-approve-note-hint"
              value={note}
              maxLength={2000}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <p id="verification-approve-note-hint" className="text-caption text-muted-foreground">
              {t('verification:approve.noteHint')}
            </p>
          </div>

          {error === null ? null : (
            <p role="alert" className="text-caption text-destructive">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('common:action.cancel')}
          </Button>
          <Button
            isLoading={isDeciding}
            onClick={() => {
              void submit();
            }}
          >
            {t('verification:approve.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
